import React from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  Snackbar,
  Alert,
} from "@mui/material";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ImageIcon from "@mui/icons-material/Image";
import RatingStars from "./RatingStars";
import InquiryModal from "./InquiryModal";
import useInquiry from "./useInquiry";

// Utility functions
const usdFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

const stripHtml = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  return div.textContent || div.innerText || '';
};

const ProductRow = ({ product, onQuickView }) => {
  
  // Use the same reusable inquiry hook
  const {
    inquiryOpen,
    snackbar,
    handleInquiryOpen,
    handleInquiryClose,
    handleInquirySubmit,
    handleSnackbarClose
  } = useInquiry();

  const handleQuickViewClick = () => {
    onQuickView(product);
  };

  return (
    <>
      <Card sx={{ 
        mb: 2,
        borderRadius: 2,
        overflow: 'hidden',
        '&:hover': {
          boxShadow: 4,
          transform: { xs: 'none', sm: 'translateY(-2px)' }
        },
        transition: 'all 0.3s ease'
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box 
            display="flex" 
            flexDirection={{ xs: 'column', sm: 'row' }} 
            gap={{ xs: 2, sm: 3 }}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          >
            {/* Product Image */}
            <Box sx={{ 
              width: { xs: '100%', sm: 120, md: 150 },
              height: { xs: 200, sm: 120, md: 150 },
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'grey.100',
              flexShrink: 0,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 1 // Add padding to ensure image doesn't touch edges
            }}>
              {product.images?.length ? (
                <Box
                  component="img"
                  src={product.images[0]}
                  alt={product.name}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain', // Changed from 'cover' to 'contain'
                    borderRadius: 1
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <ImageIcon sx={{ fontSize: 40, color: 'grey.400' }} />
              )}
              
              {/* Sale Badge */}
              {product.compareAtUsd && product.priceUsd && product.compareAtUsd > product.priceUsd && 
                <Chip 
                  label="SALE" 
                  color="error" 
                  size="small" 
                  sx={{ 
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    fontSize: '0.65rem',
                    fontWeight: 700
                  }} 
                />
              }
            </Box>

            {/* Product Details */}
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1.5, sm: 2 },
              minWidth: 0 // Important for text overflow
            }}>
              {/* Brand and Title */}
              <Box>
                <Typography variant="caption" color="primary" sx={{ 
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  {product.brand}
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 600,
                    lineHeight: 1.3,
                    mt: 0.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word'
                  }}
                >
                  {product.name}
                </Typography>
              </Box>

              {/* Rating and Category */}
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <RatingStars value={4.5} size="small" />
                <Chip 
                  label={product.category}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem' }}
                />
              </Box>

              {/* Description */}
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{
                  fontSize: '0.85rem',
                  lineHeight: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: { xs: 2, sm: 3 },
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  flex: 1
                }}
              >
                {stripHtml(product.descriptionHtml)}
              </Typography>

              {/* Price and Actions */}
              <Box 
                display="flex" 
                justifyContent="space-between" 
                alignItems={{ xs: 'stretch', sm: 'center' }}
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={{ xs: 2, sm: 3 }}
                mt="auto"
              >
                {/* Price */}
                <Box>
                  {product.priceUsd != null ? (
                    <Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontSize: { xs: '1.1rem', sm: '1.25rem' },
                          fontWeight: 700,
                          color: 'primary.main'
                        }}
                      >
                        {usdFmt.format(product.priceUsd)}
                      </Typography>
                      {product.compareAtUsd && product.compareAtUsd > product.priceUsd && 
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            textDecoration: "line-through", 
                            fontSize: '0.75rem'
                          }}
                        >
                          {usdFmt.format(product.compareAtUsd)}
                        </Typography>
                      }
                    </Box>
                  ) : (
                    <Typography 
                      variant="h6" 
                      color="text.secondary" 
                      sx={{ fontSize: '1rem', fontWeight: 500 }}
                    >
                      Price on request
                    </Typography>
                  )}
                </Box>

                {/* Action Buttons */}
                <Box 
                  display="flex" 
                  gap={1.5}
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  sx={{ minWidth: { sm: 280 } }}
                >
                  <Button 
                    variant="outlined"
                    onClick={handleQuickViewClick}
                    startIcon={<VisibilityIcon />}
                    sx={{ 
                      flex: 1,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      borderWidth: 2,
                      '&:hover': { borderWidth: 2 }
                    }}
                  >
                    View Details
                  </Button>
                  
                  <Button 
                    variant="contained"
                    onClick={handleInquiryOpen}
                    startIcon={<ShoppingBagIcon />}
                    sx={{ 
                      flex: 1,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      }
                    }}
                  >
                    Send Inquiry
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Reuse the same InquiryModal component */}
      <InquiryModal
        open={inquiryOpen}
        onClose={handleInquiryClose}
        product={product}
        onSubmit={handleInquirySubmit}
      />

      {/* Reuse the same Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductRow;